import React, { useState, useEffect } from 'react';
import { Loader2, Plus, Pencil, Trash2, ShieldCheck, UserCheck, X, Check } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api/employee-codes`;

export default function EmployeeCodesManagement({ orgId, token }) {
  const [employeeCodes, setEmployeeCodes] = useState([]);
  const [overrideCodes, setOverrideCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState('employee'); // 'employee' | 'override'
  const [editingCode, setEditingCode] = useState(null);
  const [form, setForm] = useState({ code: '', first_name: '', last_name: '', role_title: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  useEffect(() => { if (orgId) fetchAll(); }, [orgId]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [empRes, ovRes] = await Promise.all([
        fetch(`${API_URL}/${orgId}`, { headers }),
        fetch(`${API_URL}/${orgId}/overrides`, { headers }),
      ]);
      if (empRes.ok) setEmployeeCodes(await empRes.json());
      if (ovRes.ok) setOverrideCodes(await ovRes.json());
    } catch { /* silent */ }
    setLoading(false);
  };

  const openDialog = (type, code = null) => {
    setDialogType(type);
    setEditingCode(code);
    setForm(code ? { code: code.code, first_name: code.first_name || '', last_name: code.last_name || '', role_title: code.role_title || '' } : { code: '', first_name: '', last_name: '', role_title: '' });
    setError('');
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.code.trim()) { setError('Code is required'); return; }
    setSaving(true);
    setError('');
    try {
      const isOverride = dialogType === 'override';
      const basePath = isOverride ? `${API_URL}/${orgId}/overrides` : `${API_URL}/${orgId}`;
      const url = editingCode ? `${basePath}/${editingCode.id}` : basePath;
      const method = editingCode ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers, body: JSON.stringify(form) });
      if (!res.ok) {
        const d = await res.json();
        setError(d.detail || 'Save failed');
        setSaving(false);
        return;
      }
      setDialogOpen(false);
      fetchAll();
    } catch { setError('Connection error'); }
    setSaving(false);
  };

  const handleDelete = async (type, codeId) => {
    const basePath = type === 'override' ? `${API_URL}/${orgId}/overrides` : `${API_URL}/${orgId}`;
    await fetch(`${basePath}/${codeId}`, { method: 'DELETE', headers });
    fetchAll();
  };

  const toggleStatus = async (type, code) => {
    const newStatus = code.status === 'active' ? 'inactive' : 'active';
    const basePath = type === 'override' ? `${API_URL}/${orgId}/overrides` : `${API_URL}/${orgId}`;
    await fetch(`${basePath}/${code.id}`, { method: 'PUT', headers, body: JSON.stringify({ status: newStatus }) });
    fetchAll();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-6">
        <Loader2 className="w-5 h-5 animate-spin text-amber-400" />
      </div>
    );
  }

  const CodeRow = ({ code, type }) => (
    <div className="flex items-center justify-between p-2.5 bg-[hsl(217,19%,15%)] rounded-lg group" data-testid={`${type}-code-${code.code}`}>
      <div className="flex items-center space-x-3 min-w-0 flex-1">
        <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${code.status === 'active' ? 'bg-green-400' : 'bg-gray-500'}`} />
        <div className="min-w-0">
          <div className="flex items-center space-x-2">
            <span className="text-sm font-mono font-bold text-white">{code.code}</span>
            {code.first_name && <span className="text-xs text-[hsl(215,20%,60%)]">{code.first_name} {code.last_name}</span>}
            {code.role_title && <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-400">{code.role_title}</span>}
          </div>
          <span className={`text-[10px] font-medium ${code.status === 'active' ? 'text-green-400' : 'text-gray-500'}`}>
            {code.status.toUpperCase()}
          </span>
        </div>
      </div>
      <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-[hsl(215,20%,55%)] hover:text-white" onClick={() => toggleStatus(type, code)} data-testid={`toggle-${type}-${code.code}`}>
          {code.status === 'active' ? <X className="w-3.5 h-3.5" /> : <Check className="w-3.5 h-3.5" />}
        </Button>
        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-[hsl(215,20%,55%)] hover:text-white" onClick={() => openDialog(type, code)}>
          <Pencil className="w-3.5 h-3.5" />
        </Button>
        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400 hover:text-red-300" onClick={() => handleDelete(type, code.id)}>
          <Trash2 className="w-3.5 h-3.5" />
        </Button>
      </div>
    </div>
  );

  return (
    <div className="space-y-4" data-testid="employee-codes-management">
      {/* Employee Codes */}
      <div className="border border-amber-500/30 rounded-lg p-4 space-y-3 bg-amber-500/[0.04]">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <UserCheck className="w-5 h-5 text-amber-400" />
            <div>
              <p className="text-sm text-white font-medium">Employee Codes</p>
              <p className="text-xs text-[hsl(215,20%,55%)]">Staff PIN codes for hazard clearance</p>
            </div>
          </div>
          <Button size="sm" onClick={() => openDialog('employee')} className="bg-amber-600 hover:bg-amber-700 h-7 px-3 text-xs" data-testid="add-employee-code-btn">
            <Plus className="w-3 h-3 mr-1" /> Add
          </Button>
        </div>
        <div className="space-y-2 max-h-[300px] overflow-y-auto">
          {employeeCodes.length === 0 ? (
            <p className="text-center py-3 text-xs text-[hsl(215,20%,45%)]">No employee codes configured yet.</p>
          ) : (
            employeeCodes.map(c => <CodeRow key={c.id} code={c} type="employee" />)
          )}
        </div>
      </div>

      {/* Master Override Codes */}
      <div className="border border-blue-500/30 rounded-lg p-4 space-y-3 bg-blue-500/[0.04]">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <ShieldCheck className="w-5 h-5 text-blue-400" />
            <div>
              <p className="text-sm text-white font-medium">Master Override Codes</p>
              <p className="text-xs text-[hsl(215,20%,55%)]">Manager / supervisor authority codes</p>
            </div>
          </div>
          <Button size="sm" onClick={() => openDialog('override')} className="bg-blue-600 hover:bg-blue-700 h-7 px-3 text-xs" data-testid="add-override-code-btn">
            <Plus className="w-3 h-3 mr-1" /> Add
          </Button>
        </div>
        <div className="space-y-2 max-h-[300px] overflow-y-auto">
          {overrideCodes.length === 0 ? (
            <p className="text-center py-3 text-xs text-[hsl(215,20%,45%)]">No override codes configured yet.</p>
          ) : (
            overrideCodes.map(c => <CodeRow key={c.id} code={c} type="override" />)
          )}
        </div>
        <div className="p-2 bg-[hsl(217,19%,13%)] rounded-lg">
          <p className="text-[10px] text-[hsl(215,20%,50%)]">
            Global Super Admin Code <span className="font-mono font-bold text-amber-400">5992</span> is always active and works across all stores.
          </p>
        </div>
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[hsl(217,19%,12%)] border-[hsl(217,19%,27%)] text-white max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-white">
              {editingCode ? 'Edit' : 'Add'} {dialogType === 'override' ? 'Override Code' : 'Employee Code'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs text-[hsl(215,20%,65%)] mb-1 block">Code *</label>
              <Input value={form.code} onChange={e => setForm({...form, code: e.target.value})} placeholder="e.g., 4821" className="bg-[hsl(217,19%,18%)] border-[hsl(217,19%,30%)] text-white font-mono" data-testid="code-input" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs text-[hsl(215,20%,65%)] mb-1 block">First Name</label>
                <Input value={form.first_name} onChange={e => setForm({...form, first_name: e.target.value})} placeholder="John" className="bg-[hsl(217,19%,18%)] border-[hsl(217,19%,30%)] text-white" data-testid="first-name-input" />
              </div>
              <div>
                <label className="text-xs text-[hsl(215,20%,65%)] mb-1 block">Last Name</label>
                <Input value={form.last_name} onChange={e => setForm({...form, last_name: e.target.value})} placeholder="Smith" className="bg-[hsl(217,19%,18%)] border-[hsl(217,19%,30%)] text-white" data-testid="last-name-input" />
              </div>
            </div>
            {dialogType === 'override' && (
              <div>
                <label className="text-xs text-[hsl(215,20%,65%)] mb-1 block">Role / Title</label>
                <Input value={form.role_title} onChange={e => setForm({...form, role_title: e.target.value})} placeholder="Manager" className="bg-[hsl(217,19%,18%)] border-[hsl(217,19%,30%)] text-white" data-testid="role-title-input" />
              </div>
            )}
            {error && <p className="text-xs text-red-400">{error}</p>}
            <Button onClick={handleSave} disabled={saving} className="w-full bg-amber-600 hover:bg-amber-700" data-testid="save-code-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {editingCode ? 'Update' : 'Create'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
